import axios from 'axios';
import { BASE_API_URL } from '../constants/env';
import { INTERNAL_SERVICE_DELETED_SUCCESS, EXTERNAL_SERVICE_DELETED_SUCCESS } from '../constants/apiMessages';

/**
 * Obtiene los tipos de mantenimiento disponibles
 * @param {string} token - Token de autenticación del usuario
 * @returns {Promise<Array|Object>} - Lista de tipos de mantenimiento o error
 */
export const getMaintenanceTypes = async (token) => {
    const url = `${BASE_API_URL}/work-orders/maintenance-types`;

    return axios
        .get(url, {
            headers: {
                Authorization: 'Bearer ' + token,
            },
        })
        .then((response) => {
            const { data } = response;
            return data;
        })
        .catch((error) => {
            console.log('Error on getMaintenanceTypes call: ', error);
            return error;
        });
};

/**
 * Obtiene los tipos de trabajo disponibles para servicios internos
 * @param {string} token - Token de autenticación del usuario
 * @returns {Promise<Object>} - Respuesta con lista de tipos de trabajo o error
 */
export const getWorkTypes = async (token) => {
    const url = `${BASE_API_URL}/work-orders/work-types`;

    return axios
        .get(url, {
            headers: {
                Authorization: 'Bearer ' + token,
            },
        })
        .then((response) => {
            const { data } = response;
            return {
                success: true,
                data: data,
            };
        })
        .catch((error) => {
            console.log('Error on getWorkTypes call: ', error);

            if (error.response && error.response.data) {
                const errorData = error.response.data;
                const status = error.response.status;

                return {
                    success: false,
                    error: 'No se pudieron obtener los tipos de trabajo.',
                    status: status,
                    originalError: errorData,
                };
            }

            return {
                success: false,
                error: 'Error de conexión. Verifica tu internet e intenta nuevamente.',
                isNetworkError: true,
            };
        });
};

/**
 * Crea una nueva orden de trabajo
 * @param {string} token - Token de autenticación del usuario
 * @param {Object} orderData - Datos de la orden de trabajo a crear
 * @returns {Promise<Object>} - Respuesta con los datos de la orden creada o error
 */
export const createWorkOrder = async (token, orderData) => {
    const url = `${BASE_API_URL}/work-orders`;

    // Preparar el payload según la estructura requerida por la API
    const payload = {
        vehicleId: orderData.vehicleId,
        maintenanceTypeId: orderData.maintenanceTypeId,
        observations: orderData.observations,
        requesterId: orderData.requestingUserId,
        requestedDate: orderData.requestDate,
        odometer: orderData.odometer,
    };

    return axios
        .post(url, payload, {
            headers: {
                Authorization: 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
        })
        .then((response) => {
            const { data } = response;
            return {
                success: true,
                data: data,
            };
        })
        .catch((error) => {
            console.log('Error on createWorkOrder call: ', error);

            // Manejar diferentes tipos de error de respuesta
            if (error.response && error.response.data) {
                const errorData = error.response.data;

                // Si es un array de errores (validación)
                if (Array.isArray(errorData)) {
                    return {
                        success: false,
                        errors: errorData,
                        status: error.response.status,
                    };
                }

                // Si es un objeto con mensaje de error
                return {
                    success: false,
                    error: errorData.message || 'Error al crear la orden de trabajo',
                    status: error.response.status,
                };
            }

            // Error de red o conexión
            return {
                success: false,
                error: 'Error de conexión. Verifica tu internet e intenta nuevamente.',
                isNetworkError: true,
            };
        });
};

/**
 * Busca órdenes de trabajo con filtros y paginación
 * @param {string} token - Token de autenticación del usuario
 * @param {number} page - Número de página (por defecto 1)
 * @param {number} size - Cantidad de elementos por página (por defecto 10)
 * @param {string} sortField - Campo de ordenamiento (por defecto 'requestedDate,desc')
 * @param {Array} filtersData - Array de filtros a aplicar
 * @returns {Promise<Object>} - Respuesta con los datos paginados de órdenes o error
 */
export const searchWorkOrders = async (token, page = 1, size = 10, sortField = 'requestedDate,desc', filtersData = []) => {
    const url = `${BASE_API_URL}/work-orders/search?page=${page}&size=${size}&sort=${sortField}`;

    return axios
        .post(url, filtersData, {
            headers: {
                Authorization: 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
        })
        .then((response) => {
            const { data } = response;
            return data;
        })
        .catch((error) => {
            console.log('Error on searchWorkOrders call: ', error);
            return error;
        });
};

/**
 * Aprueba una orden de trabajo específica
 * @param {string} token - Token de autenticación del usuario
 * @param {number|string} orderId - ID de la orden de trabajo a aprobar
 * @returns {Promise<Object>} - Respuesta con los datos de la orden aprobada o error
 */
export const approveWorkOrder = async (token, orderId) => {
    const url = `${BASE_API_URL}/work-orders/${orderId}/approve`;

    return axios
        .post(
            url,
            {},
            {
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json',
                },
            }
        )
        .then((response) => {
            const { data } = response;
            return {
                success: true,
                data: data,
            };
        })
        .catch((error) => {
            console.log('Error on approveWorkOrder call: ', error);

            // Manejar diferentes tipos de error de respuesta
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                const status = error.response.status;

                // Para errores específicos de aprobación, devolver mensaje genérico
                return {
                    success: false,
                    error: 'No se pudo aprobar la orden de trabajo. Verifique que cumple con los requisitos necesarios.',
                    status: status,
                    originalError: errorData,
                };
            }

            // Error de red o conexión
            return {
                success: false,
                error: 'Error de conexión. Verifica tu internet e intenta nuevamente.',
                isNetworkError: true,
            };
        });
};

/**
 * Inicia el mantenimiento de una orden de trabajo específica
 * Cambia el estado de APPROVED a MAINTENANCE
 * @param {string} token - Token de autenticación del usuario
 * @param {number|string} orderId - ID de la orden de trabajo a pasar a mantenimiento
 * @returns {Promise<Object>} - Respuesta con los datos de la orden en mantenimiento o error
 */
export const startMaintenanceWorkOrder = async (token, orderId) => {
    const url = `${BASE_API_URL}/work-orders/${orderId}/start-maintenance`;

    return axios
        .post(
            url,
            {},
            {
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json',
                },
            }
        )
        .then((response) => {
            const { data } = response;
            return {
                success: true,
                data: data,
            };
        })
        .catch((error) => {
            console.log('Error on startMaintenanceWorkOrder call: ', error);

            // Manejar diferentes tipos de error de respuesta
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                const status = error.response.status;

                // Para errores específicos de cambio a mantenimiento, devolver mensaje genérico
                return {
                    success: false,
                    error: 'No se pudo iniciar el mantenimiento de la orden. Verifique que cumple con los requisitos necesarios.',
                    status: status,
                    originalError: errorData,
                };
            }

            // Error de red o conexión
            return {
                success: false,
                error: 'Error de conexión. Verifica tu internet e intenta nuevamente.',
                isNetworkError: true,
            };
        });
};

/**
 * Descarta una orden de trabajo específica
 * Elimina la orden usando DELETE y cambia su estado a DISCARDED
 * @param {string} token - Token de autenticación del usuario
 * @param {number|string} orderId - ID de la orden de trabajo a descartar
 * @returns {Promise<Object>} - Respuesta con los datos de la orden descartada o error
 */
export const discardWorkOrder = async (token, orderId) => {
    const url = `${BASE_API_URL}/work-orders/${orderId}`;

    return axios
        .delete(url, {
            headers: {
                Authorization: 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
        })
        .then((response) => {
            const { data } = response;
            return {
                success: true,
                data: data,
            };
        })
        .catch((error) => {
            console.log('Error on discardWorkOrder call: ', error);

            // Manejar diferentes tipos de error de respuesta
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                const status = error.response.status;

                return {
                    success: false,
                    error: 'No se pudo descartar la orden de trabajo. Verifique que cumple con los requisitos necesarios.',
                    status: status,
                    originalError: errorData,
                };
            }

            // Error de red o conexión
            return {
                success: false,
                error: 'Error de conexión. Verifica tu internet e intenta nuevamente.',
                isNetworkError: true,
            };
        });
};

/**
 * Finaliza una orden de trabajo específica
 * Cambia el estado de MAINTENANCE a FINALIZED
 * @param {string} token - Token de autenticación del usuario
 * @param {number|string} orderId - ID de la orden de trabajo a finalizar
 * @returns {Promise<Object>} - Respuesta con los datos de la orden finalizada o error
 */
export const finalizeWorkOrder = async (token, orderId) => {
    const url = `${BASE_API_URL}/work-orders/${orderId}/finalize`;

    return axios
        .post(
            url,
            {},
            {
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json',
                },
            }
        )
        .then((response) => {
            const { data } = response;
            return {
                success: true,
                data: data,
            };
        })
        .catch((error) => {
            console.log('Error on finalizeWorkOrder call: ', error);

            // Manejar diferentes tipos de error de respuesta
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                const status = error.response.status;

                // Para errores específicos de finalización, devolver mensaje genérico
                return {
                    success: false,
                    error: 'No se pudo finalizar la orden de trabajo. Verifique que cumple con los requisitos necesarios.',
                    status: status,
                    originalError: errorData,
                };
            }

            // Error de red o conexión
            return {
                success: false,
                error: 'Error de conexión. Verifica tu internet e intenta nuevamente.',
                isNetworkError: true,
            };
        });
};

/**
 * Obtiene los detalles completos de una orden de trabajo específica
 * @param {string} token - Token de autenticación del usuario
 * @param {number|string} orderId - ID de la orden de trabajo
 * @returns {Promise<Object>} - Respuesta con los datos completos de la orden o error
 */
export const getWorkOrder = async (token, orderId) => {
    const url = `${BASE_API_URL}/work-orders/${orderId}`;

    return axios
        .get(url, {
            headers: {
                Authorization: 'Bearer ' + token,
            },
        })
        .then((response) => {
            const { data } = response;
            return {
                success: true,
                data: data,
            };
        })
        .catch((error) => {
            console.log('Error on getWorkOrder call: ', error);

            if (error.response && error.response.data) {
                const errorData = error.response.data;
                const status = error.response.status;

                return {
                    success: false,
                    error: 'No se pudo obtener la información de la orden de trabajo.',
                    status: status,
                    originalError: errorData,
                };
            }

            return {
                success: false,
                error: 'Error de conexión. Verifica tu internet e intenta nuevamente.',
                isNetworkError: true,
            };
        });
};

/**
 * Edita una orden de trabajo existente
 * Solo envía los campos que hayan cambiado respecto a los datos originales
 * @param {string} token - Token de autenticación del usuario
 * @param {number|string} orderId - ID de la orden de trabajo a editar
 * @param {Object} updatedData - Datos actualizados de la orden
 * @param {Object} originalData - Datos originales de la orden para comparar
 * @returns {Promise<Object>} - Respuesta con los datos de la orden editada o error
 */
export const editWorkOrder = async (token, orderId, updatedData, originalData) => {
    const url = `${BASE_API_URL}/work-orders/${orderId}`;

    // Construir payload solo con campos modificados
    const payload = {};

    // Comparar y agregar solo campos que cambiaron
    if (updatedData.requestingUserId !== originalData.requesterId) {
        payload.requesterId = updatedData.requestingUserId;
    }

    if (updatedData.maintenanceTypeId !== originalData.maintenanceTypeId) {
        payload.maintenanceTypeId = updatedData.maintenanceTypeId;
    }

    if (updatedData.instructions !== originalData.instructions) {
        payload.instructions = updatedData.instructions;
    }

    if (updatedData.odometer !== originalData.odometer) {
        payload.odometer = updatedData.odometer;
    }

    // Si no hay cambios, devolver éxito sin hacer la llamada
    if (Object.keys(payload).length === 0) {
        return {
            success: true,
            data: originalData,
            noChanges: true,
        };
    }

    return axios
        .put(url, payload, {
            headers: {
                Authorization: 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
        })
        .then((response) => {
            const { data } = response;
            return {
                success: true,
                data: data,
            };
        })
        .catch((error) => {
            console.log('Error on editWorkOrder call: ', error);

            // Manejar diferentes tipos de error de respuesta
            if (error.response && error.response.data) {
                const status = error.response.status;

                return {
                    success: false,
                    error: 'No se pudo editar la orden de trabajo. Verifique que los datos ingresados sean válidos.',
                    status: status,
                };
            }

            // Error de red o conexión
            return {
                success: false,
                error: 'Error de conexión. Verifica tu internet e intenta nuevamente.',
                isNetworkError: true,
            };
        });
};

/**
 * Crea un nuevo servicio interno en una orden de trabajo
 * @param {string} token - Token de autenticación del usuario
 * @param {number|string} workOrderId - ID de la orden de trabajo
 * @param {Object} serviceData - Datos del servicio interno a crear
 * @returns {Promise<Object>} - Respuesta con los datos del servicio creado o error
 */
export const createInternalService = async (token, workOrderId, serviceData) => {
    const url = `${BASE_API_URL}/work-orders/${workOrderId}/performed-works`;

    const payload = {
        workTypeId: serviceData.workTypeId,
        workSubtypeId: serviceData.workSubtypeId,
        startTime: serviceData.startTime,
        endTime: serviceData.endTime || null,
        hoursSpent: serviceData.hoursSpent ? Number(serviceData.hoursSpent) : null,
        mechanicId: serviceData.mechanicId,
        description: serviceData.description,
        totalAmount: serviceData.totalAmount ? Number(serviceData.totalAmount) : null,
    };

    return axios
        .post(url, payload, {
            headers: {
                Authorization: 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
        })
        .then((response) => {
            const { data } = response;
            return {
                success: true,
                data: data,
            };
        })
        .catch((error) => {
            console.log('Error on createInternalService call: ', error);

            if (error.response && error.response.data) {
                const status = error.response.status;

                return {
                    success: false,
                    error: 'No se pudo crear el servicio interno.',
                    status: status,
                };
            }

            return {
                success: false,
                error: 'Error de conexión. Verifica tu internet e intenta nuevamente.',
                isNetworkError: true,
            };
        });
};

/**
 * Edita un servicio interno existente en una orden de trabajo
 * @param {string} token - Token de autenticación del usuario
 * @param {number|string} workOrderId - ID de la orden de trabajo
 * @param {number|string} serviceId - ID del servicio interno a editar
 * @param {Object} serviceData - Datos actualizados del servicio interno
 * @returns {Promise<Object>} - Respuesta con los datos del servicio editado o error
 */
export const editInternalService = async (token, workOrderId, serviceId, serviceData) => {
    const url = `${BASE_API_URL}/work-orders/${workOrderId}/performed-works/${serviceId}`;

    const payload = {
        workTypeId: serviceData.workTypeId,
        workSubtypeId: serviceData.workSubtypeId,
        startTime: serviceData.startTime,
        endTime: serviceData.endTime || null,
        hoursSpent: serviceData.hoursSpent ? Number(serviceData.hoursSpent) : null,
        mechanicId: serviceData.mechanicId,
        description: serviceData.description,
        totalAmount: serviceData.totalAmount ? Number(serviceData.totalAmount) : null,
    };

    return axios
        .put(url, payload, {
            headers: {
                Authorization: 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
        })
        .then((response) => {
            const { data } = response;
            return {
                success: true,
                data: data,
            };
        })
        .catch((error) => {
            console.log('Error on editInternalService call: ', error);

            if (error.response && error.response.data) {
                const status = error.response.status;

                return {
                    success: false,
                    error: 'No se pudo editar el servicio interno.',
                    status: status,
                };
            }

            return {
                success: false,
                error: 'Error de conexión. Verifica tu internet e intenta nuevamente.',
                isNetworkError: true,
            };
        });
};

/**
 * Elimina un servicio interno de una orden de trabajo
 * @param {string} token - Token de autenticación del usuario
 * @param {number|string} workOrderId - ID de la orden de trabajo
 * @param {number|string} serviceId - ID del servicio interno a eliminar
 * @returns {Promise<Object>} - Respuesta con éxito o error
 */
export const deleteInternalService = async (token, workOrderId, serviceId) => {
    const url = `${BASE_API_URL}/work-orders/${workOrderId}/performed-works/${serviceId}`;

    return axios
        .delete(url, {
            headers: {
                Authorization: 'Bearer ' + token,
            },
        })
        .then((response) => {
            // Validar que el mensaje de respuesta sea exactamente el esperado
            if (response.data === INTERNAL_SERVICE_DELETED_SUCCESS) {
                return {
                    success: true,
                    message: response.data,
                };
            } else {
                // Si el mensaje no coincide, es un error
                return {
                    success: false,
                    error: 'No se pudo eliminar el servicio interno.',
                    unexpectedResponse: response.data,
                };
            }
        })
        .catch((error) => {
            console.log('Error on deleteInternalService call: ', error);

            if (error.response && error.response.data) {
                const status = error.response.status;

                return {
                    success: false,
                    error: 'No se pudo eliminar el servicio interno.',
                    status: status,
                };
            }

            return {
                success: false,
                error: 'Error de conexión. Verifica tu internet e intenta nuevamente.',
                isNetworkError: true,
            };
        });
};

/**
 * Crea un nuevo servicio externo en una orden de trabajo
 * @param {string} token - Token de autenticación del usuario
 * @param {number|string} workOrderId - ID de la orden de trabajo
 * @param {Object} serviceData - Datos del servicio externo a crear
 * @returns {Promise<Object>} - Respuesta con los datos del servicio creado o error
 */
export const createExternalService = async (token, workOrderId, serviceData) => {
    const url = `${BASE_API_URL}/work-orders/${workOrderId}/external-works`;

    const payload = {
        workTypeId: serviceData.workTypeId,
        workSubtypeId: serviceData.workSubtypeId,
        startTime: serviceData.startTime,
        endTime: serviceData.endTime || null,
        hoursSpent: serviceData.hoursSpent ? Number(serviceData.hoursSpent) : null,
        mechanicId: serviceData.mechanicId,
        description: serviceData.description,
        totalAmount: serviceData.totalAmount ? Number(serviceData.totalAmount) : null,
    };

    return axios
        .post(url, payload, {
            headers: {
                Authorization: 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
        })
        .then((response) => {
            const { data } = response;
            return {
                success: true,
                data: data,
            };
        })
        .catch((error) => {
            console.log('Error on createExternalService call: ', error);

            if (error.response && error.response.data) {
                const status = error.response.status;

                return {
                    success: false,
                    error: 'No se pudo crear el servicio externo.',
                    status: status,
                };
            }

            return {
                success: false,
                error: 'Error de conexión. Verifica tu internet e intenta nuevamente.',
                isNetworkError: true,
            };
        });
};

/**
 * Edita un servicio externo existente en una orden de trabajo
 * @param {string} token - Token de autenticación del usuario
 * @param {number|string} workOrderId - ID de la orden de trabajo
 * @param {number|string} serviceId - ID del servicio externo a editar
 * @param {Object} serviceData - Datos actualizados del servicio externo
 * @returns {Promise<Object>} - Respuesta con los datos del servicio editado o error
 */
export const editExternalService = async (token, workOrderId, serviceId, serviceData) => {
    const url = `${BASE_API_URL}/work-orders/${workOrderId}/external-works/${serviceId}`;

    const payload = {
        workTypeId: serviceData.workTypeId,
        workSubtypeId: serviceData.workSubtypeId,
        startTime: serviceData.startTime,
        endTime: serviceData.endTime || null,
        hoursSpent: serviceData.hoursSpent ? Number(serviceData.hoursSpent) : null,
        mechanicId: serviceData.mechanicId,
        description: serviceData.description,
        totalAmount: serviceData.totalAmount ? Number(serviceData.totalAmount) : null,
    };

    return axios
        .put(url, payload, {
            headers: {
                Authorization: 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
        })
        .then((response) => {
            const { data } = response;
            return {
                success: true,
                data: data,
            };
        })
        .catch((error) => {
            console.log('Error on editExternalService call: ', error);

            if (error.response && error.response.data) {
                const status = error.response.status;

                return {
                    success: false,
                    error: 'No se pudo editar el servicio externo.',
                    status: status,
                };
            }

            return {
                success: false,
                error: 'Error de conexión. Verifica tu internet e intenta nuevamente.',
                isNetworkError: true,
            };
        });
};

/**
 * Elimina un servicio externo de una orden de trabajo
 * @param {string} token - Token de autenticación del usuario
 * @param {number|string} workOrderId - ID de la orden de trabajo
 * @param {number|string} serviceId - ID del servicio externo a eliminar
 * @returns {Promise<Object>} - Respuesta con éxito o error
 */
export const deleteExternalService = async (token, workOrderId, serviceId) => {
    const url = `${BASE_API_URL}/work-orders/${workOrderId}/external-works/${serviceId}`;

    return axios
        .delete(url, {
            headers: {
                Authorization: 'Bearer ' + token,
            },
        })
        .then((response) => {
            if (response.data === EXTERNAL_SERVICE_DELETED_SUCCESS) {
                return {
                    success: true,
                    message: response.data,
                };
            } else {
                return {
                    success: false,
                    error: 'No se pudo eliminar el servicio externo.',
                    unexpectedResponse: response.data,
                };
            }
        })
        .catch((error) => {
            console.log('Error on deleteExternalService call: ', error);

            if (error.response && error.response.data) {
                const status = error.response.status;

                return {
                    success: false,
                    error: 'No se pudo eliminar el servicio externo.',
                    status: status,
                };
            }

            return {
                success: false,
                error: 'Error de conexión. Verifica tu internet e intenta nuevamente.',
                isNetworkError: true,
            };
        });
};
